import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Inventory2Outlined, Search } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService, Product } from '../services/api';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useToast } from '../contexts/ToastContext';
import {
  ConfirmDialog,
  DataTableShell,
  NumberField,
  EmptyState,
  FormDialog,
  PageContainer,
  PageHeader,
  StatusChip,
  TableSkeleton,
} from '../components/ui';

interface BundleComponent {
  id: string;
  name?: string;
  sku?: string;
  brand?: string;
}

interface StoreBundle {
  id: string;
  organizationId: string;
  componentCanonicalProductIds: string[];
  storeSku?: string;
  storePrice?: number;
  components: BundleComponent[];
  createdAt: string;
  updatedAt: string;
}

interface BundleFormState {
  components: BundleComponent[];
  storeSku: string;
  storePrice: string;
}

const emptyForm = (): BundleFormState => ({
  components: [],
  storeSku: '',
  storePrice: '',
});

const formatPrice = (value?: number): string => {
  if (typeof value !== 'number') return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value);
};

const productToComponent = (product: Product): BundleComponent => ({
  id: product.id,
  name: product.name || product.model,
  sku: product.sku,
  brand: product.brand,
});

const componentLabel = (component: BundleComponent): string => {
  const title = component.name || component.id;
  const sku = component.sku ? ` · ${component.sku}` : '';
  const brand = component.brand ? `${component.brand} ` : '';
  return `${brand}${title}${sku}`;
};

export const StoreBundlesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<StoreBundle | null>(null);
  const [form, setForm] = useState<BundleFormState>(emptyForm());
  const [deleteTarget, setDeleteTarget] = useState<StoreBundle | null>(null);
  const [productQuery, setProductQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(productQuery.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [productQuery]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['storeBundles', page, rowsPerPage, search],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        limit: String(rowsPerPage),
        offset: String(page * rowsPerPage),
        ...(search ? { search } : {}),
      });
      const response = await makeApiCall(`/store/bundles?${params}`, { headers });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load store bundles');
      }
      return response.json() as Promise<{ bundles: StoreBundle[]; total: number }>;
    },
  });

  const {
    data: productHits,
    isFetching: productsLoading,
    isError: productsError,
  } = useQuery({
    queryKey: ['storeBundleProductSearch', debouncedQuery],
    enabled: formOpen && debouncedQuery.length >= 2,
    queryFn: async () => {
      const result = await apiService.getStoreProducts(20, 0, { search: debouncedQuery });
      return result.data || [];
    },
  });

  const productOptions = useMemo(() => {
    const hits = productHits || [];
    const selectedIds = new Set(form.components.map((component) => component.id));
    return [
      ...form.components,
      ...hits
        .filter((product) => !selectedIds.has(product.id))
        .map(productToComponent),
    ];
  }, [form.components, productHits]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (form.components.length < 2) {
        throw new Error('Pick at least two catalog products');
      }
      const headers = await getAuthHeaders();
      const payload: Record<string, unknown> = {
        componentCanonicalProductIds: form.components.map((component) => component.id),
      };
      const parsedPrice = form.storePrice.trim() ? Number(form.storePrice) : undefined;
      if (parsedPrice !== undefined && (!Number.isFinite(parsedPrice) || parsedPrice < 0)) {
        throw new Error('Store price must be a number of 0 or more');
      }
      if (editing) {
        payload.storeSku = form.storeSku.trim() || null;
        payload.storePrice = parsedPrice ?? null;
      } else {
        if (form.storeSku.trim()) payload.storeSku = form.storeSku.trim();
        if (parsedPrice !== undefined) payload.storePrice = parsedPrice;
      }

      const response = await makeApiCall(
        editing ? `/store/bundles/${encodeURIComponent(editing.id)}` : '/store/bundles',
        {
          method: editing ? 'PUT' : 'POST',
          headers,
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save store bundle');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storeBundles'] });
      showToast(editing ? 'Bundle updated' : 'Bundle created', 'success');
      setFormOpen(false);
      setEditing(null);
      setForm(emptyForm());
    },
    onError: (err: Error) => {
      showToast(err.message, 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (bundle: StoreBundle) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/store/bundles/${encodeURIComponent(bundle.id)}`, {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to delete store bundle');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storeBundles'] });
      showToast('Bundle deleted', 'success');
      setDeleteTarget(null);
    },
    onError: (err: Error) => {
      showToast(err.message, 'error');
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setProductQuery('');
    setFormOpen(true);
  };

  const openEdit = (bundle: StoreBundle) => {
    setEditing(bundle);
    setForm({
      components: bundle.components?.length
        ? bundle.components
        : bundle.componentCanonicalProductIds.map((id) => ({ id })),
      storeSku: bundle.storeSku || '',
      storePrice: typeof bundle.storePrice === 'number' ? String(bundle.storePrice) : '',
    });
    setProductQuery('');
    setFormOpen(true);
  };

  const bundles = data?.bundles || [];
  const total = data?.total || 0;

  return (
    <PageContainer>
      <PageHeader
        title="Store Bundles"
        description="Create kits from products in your catalog (e.g. body + lens). Each item keeps its own barcode and MAP."
        actionLabel="New bundle"
        actionIcon={<Add />}
        onAction={openCreate}
      />

      <DataTableShell
        toolbar={
          !isLoading && !error && bundles.length === 0 && !search ? undefined : (
          <TextField
            size="small"
            placeholder="Search store SKU or product ID"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            InputProps={{ startAdornment: <Search fontSize="small" /> }}
            sx={{ maxWidth: 360 }}
          />
          )
        }
      >
        {isLoading ? (
          <TableSkeleton rows={6} columns={5} />
        ) : error ? (
          <EmptyState
            title="Could not load bundles"
            description={error instanceof Error ? error.message : 'Try again in a moment.'}
          />
        ) : bundles.length === 0 ? (
          <EmptyState
            icon={<Inventory2Outlined />}
            title="No store bundles yet"
            description="Group two or more catalog products into a kit. You can also give the kit your own SKU, like KB15994G."
          />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Store SKU</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Store price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bundles.map((bundle) => (
                    <TableRow key={bundle.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {bundle.storeSku || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          {(bundle.components?.length
                            ? bundle.components
                            : bundle.componentCanonicalProductIds.map((id) => ({ id }))
                          ).map((component) => (
                            <Typography key={component.id} variant="body2">
                              {componentLabel(component)}
                            </Typography>
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>{formatPrice(bundle.storePrice)}</TableCell>
                      <TableCell>
                        <StatusChip
                          label={`${bundle.componentCanonicalProductIds.length} products`}
                          tone="info"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(bundle)} aria-label="Edit bundle">
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(bundle)}
                          aria-label="Delete bundle"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_event, next) => setPage(next)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </DataTableShell>

      <FormDialog
        open={formOpen}
        title={editing ? 'Edit store bundle' : 'New store bundle'}
        onClose={() => {
          if (saveMutation.isPending) return;
          setFormOpen(false);
        }}
        onSubmit={() => saveMutation.mutate()}
        submitLabel={editing ? 'Save bundle' : 'Create bundle'}
        submitDisabled={saveMutation.isPending || form.components.length < 2}
        maxWidth="md"
        disableEnforceFocus
      >
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Autocomplete
            multiple
            fullWidth
            filterSelectedOptions
            options={productOptions}
            value={form.components}
            inputValue={productQuery}
            loading={productsLoading}
            filterOptions={(options) => options}
            getOptionLabel={(option) => componentLabel(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={
              productQuery.trim().length < 2
                ? 'Type at least 2 characters to search the catalog'
                : productsError
                  ? 'Could not search products. Try again.'
                  : 'No matching products'
            }
            onChange={(_event, value) => {
              setForm((current) => ({ ...current, components: value }));
              setProductQuery('');
            }}
            onInputChange={(_event, value, reason) => {
              if (reason === 'reset') return;
              setProductQuery(value);
            }}
            slotProps={{
              popper: { sx: { zIndex: (theme) => theme.zIndex.modal + 1 } },
            }}
            sx={{ '& .MuiAutocomplete-input': { minWidth: '12ch' } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Catalog products"
                placeholder="Type a model, SKU, or product name"
                helperText="Search your catalog and pick at least two products. Official manufacturer kits stay in the catalog."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {productsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            label="Store SKU (optional)"
            value={form.storeSku}
            onChange={(event) => setForm((current) => ({ ...current, storeSku: event.target.value }))}
            placeholder="KB15994G"
            helperText="Your own SKU for this kit. Don't enter a manufacturer barcode here."
          />
          <NumberField
            label="Store price (optional)"
            size="medium"
            value={form.storePrice}
            onChange={(next) => setForm((current) => ({ ...current, storePrice: next }))}
            min={0}
            step={0.01}
            adornment="$"
          />
          <Box>
            <Typography variant="overline">Barcodes</Typography>
            <Typography variant="body2" color="text.secondary">
              The kit has no barcode of its own. Each item keeps its own barcode and MAP.
            </Typography>
          </Box>
        </Stack>
      </FormDialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete this bundle?"
        message={
          deleteTarget
            ? `Remove ${deleteTarget.storeSku || 'this kit'}? The products in your catalog are unchanged.`
            : ''
        }
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget);
        }}
        onClose={() => {
          if (!deleteMutation.isPending) setDeleteTarget(null);
        }}
      />
    </PageContainer>
  );
};

export default StoreBundlesPage;
